import { Group, Chip, Text, Button, Divider } from "@mantine/core";
import {
  IconSparkles,
  IconFlask,
  IconAlertTriangle,
  IconClock,
  IconMessageCircle,
  IconBiohazard,
  IconShieldCheck,
  IconTrendingUp,
  IconX,
} from "@tabler/icons-react";
import type { PathologistReviewStats } from "../../services";

export type SummaryFilterType =
  | "total"
  | "new"
  | "stat"
  | "overdue"
  | "pendingStains"
  | "discussion"
  | "malignancy"
  | "qaReview";

interface SummaryBarProps {
  stats: PathologistReviewStats | undefined;
  activeSummaryFilter: SummaryFilterType | null;
  onStatClick: (filter: SummaryFilterType) => void;
  onClear: () => void;
}

interface ClickableChipProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  active: boolean;
  onClick: () => void;
}

function ClickableChip({
  label,
  value,
  icon,
  color,
  active,
  onClick,
}: ClickableChipProps) {
  return (
    <Chip
      size="xs"
      checked={active}
      onChange={onClick}
      color={color}
      variant={active ? "filled" : "outline"}
      styles={{ label: { paddingInline: 8 } }}
    >
      <Group gap={4} wrap="nowrap" align="center">
        {icon}
        <Text
          span
          size="xs"
          c={active ? undefined : "dimmed"}
          style={{ whiteSpace: "nowrap" }}
        >
          {label}:
        </Text>
        <Text span size="xs" fw={700} style={{ whiteSpace: "nowrap" }}>
          {value}
        </Text>
      </Group>
    </Chip>
  );
}

interface MetricTextProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
}

function MetricText({ label, value, icon, color }: MetricTextProps) {
  return (
    <Group
      gap={4}
      wrap="nowrap"
      align="center"
      style={{ color: `var(--mantine-color-${color}-6)` }}
    >
      {icon}
      <Text size="xs" c="dimmed" style={{ whiteSpace: "nowrap" }}>
        {label}:
      </Text>
      <Text size="xs" fw={700} style={{ whiteSpace: "nowrap" }}>
        {value}
      </Text>
    </Group>
  );
}

export function SummaryBar({
  stats,
  activeSummaryFilter,
  onStatClick,
  onClear,
}: SummaryBarProps) {
  const newStat = stats?.new ?? 0;
  const pendingStainsStat = stats?.pendingStains ?? 0;
  const discussionStat = stats?.discussion ?? 0;

  const statStat =
    (stats?.rushOnly ?? 0) +
    (stats?.rushAndPriority ?? 0) +
    (stats?.rushAndOverdue ?? 0) +
    (stats?.allThree ?? 0);

  const overdueStat =
    (stats?.overdueOnly ?? 0) +
    (stats?.rushAndOverdue ?? 0) +
    (stats?.priorityAndOverdue ?? 0) +
    (stats?.allThree ?? 0);

  const malignancyStat = stats?.malignancy ?? 0;
  const qaReviewStat = stats?.qaReview ?? 0;
  const tatAvgDays = stats?.tatAvgDays ?? 0;

  const hasActiveFilter = activeSummaryFilter !== null;

  return (
    <Group gap={4} wrap="wrap" align="center" style={{ minWidth: 0 }}>
      <MetricText
        label="TAT Avg"
        value={`${tatAvgDays.toFixed(2)} days`}
        icon={<IconTrendingUp size={12} />}
        color="blue"
      />
      <Divider
        orientation="vertical"
        style={{ height: 16, alignSelf: "center" }}
      />
      {newStat > 0 && (
        <ClickableChip
          label="New"
          value={newStat}
          icon={<IconSparkles size={12} />}
          color="green"
          active={activeSummaryFilter === "new"}
          onClick={() => onStatClick("new")}
        />
      )}
      {statStat > 0 && (
        <ClickableChip
          label="STAT"
          value={statStat}
          icon={<IconAlertTriangle size={12} />}
          color="red"
          active={activeSummaryFilter === "stat"}
          onClick={() => onStatClick("stat")}
        />
      )}
      {overdueStat > 0 && (
        <ClickableChip
          label="Overdue"
          value={overdueStat}
          icon={<IconClock size={12} />}
          color="yellow"
          active={activeSummaryFilter === "overdue"}
          onClick={() => onStatClick("overdue")}
        />
      )}
      {pendingStainsStat > 0 && (
        <ClickableChip
          label="Pending Stains"
          value={pendingStainsStat}
          icon={<IconFlask size={12} />}
          color="orange"
          active={activeSummaryFilter === "pendingStains"}
          onClick={() => onStatClick("pendingStains")}
        />
      )}
      {discussionStat > 0 && (
        <ClickableChip
          label="Discussion"
          value={discussionStat}
          icon={<IconMessageCircle size={12} />}
          color="grape"
          active={activeSummaryFilter === "discussion"}
          onClick={() => onStatClick("discussion")}
        />
      )}
      {malignancyStat > 0 && (
        <ClickableChip
          label="Malignancy"
          value={malignancyStat}
          icon={<IconBiohazard size={12} />}
          color="red"
          active={activeSummaryFilter === "malignancy"}
          onClick={() => onStatClick("malignancy")}
        />
      )}
      {qaReviewStat > 0 && (
        <ClickableChip
          label="QA Review"
          value={qaReviewStat}
          icon={<IconShieldCheck size={12} />}
          color="cyan"
          active={activeSummaryFilter === "qaReview"}
          onClick={() => onStatClick("qaReview")}
        />
      )}
      {hasActiveFilter && (
        <Button
          size="compact-xs"
          variant="subtle"
          color="gray"
          rightSection={<IconX size={11} />}
          onClick={onClear}
        >
          Clear
        </Button>
      )}
    </Group>
  );
}
